/**
 * StockReport — o que eu tenho, o que está acabando, o que girou no período e o
 * que está parado.
 *
 * Portões (atenção, é DIFERENTE do relatório de pagamentos):
 * - **Plano** (`inventory`, PRO): usuário grátis vê o padrão da página —
 *   conteúdo borrado com amostra + CTA, nunca uma tela vazia.
 * - **SEM portão de papel.** A RLS de `stock_movements` é SELECT para qualquer
 *   MEMBRO da empresa (ao contrário de `finance_entries`, que é owner/admin),
 *   então um `member` recebe as linhas normalmente e deve ver o relatório.
 *
 * ⚠️ "Valor investido" (saldo × custo) e "Potencial de venda" (saldo × preço) são
 * coisas diferentes e aparecem os dois, rotulados. Como `products.cost` é
 * nullable, o investido informa quantos itens ficaram de fora por não terem custo
 * — ver `stockReport.ts`.
 *
 * A agregação toda vive em `@/lib/stockReport` (puro/testável); aqui só há
 * apresentação.
 */
import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button3D } from '@/components/ui/button-3d';
import {
  AlertTriangle,
  ArrowDownCircle,
  ArrowUpCircle,
  BarChart3,
  Boxes,
  Crown,
  PauseCircle,
  Tag,
  TrendingDown,
  Wallet,
  WifiOff,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatBRL, formatBRLShort } from '@/lib/financeLabels';
import {
  AwaitingData,
  BlurOverlay,
  ListTail,
} from '@/components/reports/ReportGates';
import {
  listLimitFor,
  ReportViewToggle,
  type ReportViewMode,
} from '@/components/reports/ReportViewToggle';
import {
  aggregateMovementsByReason,
  alertBuckets,
  balanceOf,
  formatQty,
  idleItems,
  movementTotals,
  stockPositionSummary,
  stockUnitLabel,
  STOCK_ALERT_BADGE_LABELS,
  topMovedItems,
  type StockAlertKind,
  type StockMovementRow,
  type StockUnitRow,
} from '@/lib/stockReport';

/**
 * Quantos itens as listas longas mostram no modo RESUMO antes do "+N". No modo
 * Detalhado não há corte — era justamente este "+N … veja na exportação" que
 * empurrava o usuário para fora da tela.
 */
const LIST_LIMIT = 8;

// ===== amostra para o usuário grátis (será borrada) =====

const SAMPLE_REASONS = [
  { reason: 'purchase', label: 'Entrada por compra', shortLabel: 'Compra', inQty: 120, outQty: 0, net: 120, count: 6 },
  { reason: 'quote_sale', label: 'Baixa por orçamento', shortLabel: 'Orçamento', inQty: 0, outQty: 84, net: -84, count: 21 },
  { reason: 'manual_out', label: 'Saída manual', shortLabel: 'Saída', inQty: 0, outQty: 18, net: -18, count: 5 },
  { reason: 'adjustment', label: 'Ajuste de estoque', shortLabel: 'Ajuste', inQty: 4, outQty: 7, net: -3, count: 3 },
];

const SAMPLE_MOVED = [
  { productId: 's1', name: 'Camiseta › P', outQty: 32, inQty: 0, net: -32, count: 9 },
  { productId: 's2', name: 'Cabo HDMI', outQty: 24, inQty: 10, net: -14, count: 7 },
  { productId: 's3', name: 'Tinta branca 18L', outQty: 12, inQty: 0, net: -12, count: 4 },
];

interface StockReportProps {
  /**
   * Unidades ESTOCÁVEIS (a variação, nunca o pai) — o chamador já filtrou com
   * `isStockableUnit` via `useStock().trackedProducts`.
   */
  units: StockUnitRow[];
  /** Todos os produtos por id, para resolver o nome do pai das variações. */
  productById: Map<string, { name?: string | null }>;
  /** Movimentos do período (leitura própria, paginada — nunca o hook de 500). */
  movements: StockMovementRow[];
  /** A leitura bateu no teto de páginas: a tela precisa avisar. */
  truncated?: boolean;
  loading?: boolean;
  isOnline?: boolean;
  viewMode: ReportViewMode;
  onViewModeChange: (mode: ReportViewMode) => void;
  /** `inventory.allowed`. */
  allowed: boolean;
  onUnlock: () => void;
}

export function StockReport({
  units,
  productById,
  movements,
  truncated = false,
  loading = false,
  isOnline = true,
  viewMode,
  onViewModeChange,
  allowed,
  onUnlock,
}: StockReportProps) {
  const listLimit = listLimitFor(viewMode, LIST_LIMIT);
  /** Rótulo pronto por id ("Camiseta › P"), usado na lista e no ranking. */
  const labelOf = useMemo(() => {
    const cache = new Map<string, string>();
    for (const unit of units) {
      const parentName = unit.parent_product_id
        ? productById.get(String(unit.parent_product_id))?.name ?? null
        : null;
      cache.set(String(unit.id), stockUnitLabel(unit, parentName));
    }
    return cache;
  }, [units, productById]);

  const summary = useMemo(() => stockPositionSummary(units), [units]);
  const byReason = useMemo(() => aggregateMovementsByReason(movements), [movements]);
  const totals = useMemo(() => movementTotals(movements), [movements]);
  const moved = useMemo(
    () => topMovedItems(movements, labelOf, listLimit),
    [movements, labelOf, listLimit],
  );
  const idle = useMemo(() => idleItems(units, movements), [units, movements]);
  const alerts = useMemo(() => alertBuckets(units), [units]);

  const nameOf = (unit: StockUnitRow) => labelOf.get(String(unit.id)) ?? stockUnitLabel(unit);

  /** Tick do eixo Y: quantidade, não moeda — nada de "R$" nem centavos. */
  const axisTick = (value: number) =>
    Math.abs(value) >= 1000 ? `${Math.round(value / 1000)}K` : String(Math.round(value));

  const Gate = ({ children }: { children: React.ReactNode }) => (
    <BlurOverlay allowed={allowed} onUnlock={onUnlock}>
      {children}
    </BlurOverlay>
  );

  const hasMovements = byReason.length > 0;

  return (
    <div className="space-y-6">
      {/* A posição é de AGORA; só o resto respeita o período. Dizer isso evita a
          leitura errada de "tenho 12 itens no período selecionado". */}
      <Card className="bg-muted/30">
        <CardContent className="p-3">
          <p className="text-[11px] leading-snug text-muted-foreground">
            <span className="font-medium">Posição</span> é a fotografia do estoque de agora.{' '}
            <span className="font-medium">Movimentação, giro e itens parados</span> respeitam o
            período selecionado acima.
          </p>
        </CardContent>
      </Card>

      {truncated && allowed && (
        <Card className="border-amber-300/60 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/10">
          <CardContent className="flex items-start gap-2 p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs leading-snug">
              O período tem movimentos demais e a leitura foi limitada. Os números abaixo estão
              incompletos — escolha um período menor.
            </p>
          </CardContent>
        </Card>
      )}

      {allowed && (
        <ReportViewToggle
          value={viewMode}
          onChange={onViewModeChange}
          hint={`${units.length} ${units.length === 1 ? 'item rastreado' : 'itens rastreados'}`}
        />
      )}

      {!isOnline && allowed && (
        <Card className="bg-muted/30">
          <CardContent className="flex items-start gap-2 p-3">
            <WifiOff className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-xs leading-snug text-muted-foreground">
              Sem conexão: a <span className="font-medium">posição</span> vem do que está salvo no
              aparelho, mas a movimentação do período precisa de internet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* ===== 1. Posição atual ===== */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          icon={<Boxes className="h-3.5 w-3.5 text-blue-600" />}
          tint="bg-blue-100 dark:bg-blue-950/30"
          label="Itens rastreados"
          value={allowed ? String(summary.trackedCount) : '24'}
          valueClass="text-blue-600"
          hint="produtos com estoque"
          allowed={allowed}
          onUnlock={onUnlock}
        />
        <KpiCard
          icon={<AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}
          tint="bg-amber-100 dark:bg-amber-950/30"
          label="Abaixo do mínimo"
          value={allowed ? String(summary.lowCount) : '5'}
          valueClass="text-amber-600"
          hint="repor em breve"
          allowed={allowed}
          onUnlock={onUnlock}
        />
        <KpiCard
          icon={<TrendingDown className="h-3.5 w-3.5 text-muted-foreground" />}
          tint="bg-muted"
          label="Zerados"
          value={allowed ? String(summary.zeroCount) : '3'}
          valueClass=""
          hint="sem saldo"
          allowed={allowed}
          onUnlock={onUnlock}
        />
        <KpiCard
          icon={<AlertTriangle className="h-3.5 w-3.5 text-red-600" />}
          tint="bg-red-100 dark:bg-red-950/30"
          label="Negativos"
          value={allowed ? String(summary.negativeCount) : '1'}
          valueClass="text-red-600"
          hint="saldo devendo"
          allowed={allowed}
          onUnlock={onUnlock}
        />
      </div>

      {/* DOIS valores, rótulos HONESTOS: o que está investido (custo) e o que
          pode entrar (preço de venda). Nunca "valor em estoque" solto, que
          confunde os dois. Como `cost` é nullable, o investido avisa quantos
          itens ficaram de fora — corte silencioso é proibido. */}
      <Card className="bg-muted/30">
        <CardContent className="space-y-3 p-4">
          <Gate>
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <Wallet className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="text-sm text-muted-foreground">Valor investido</p>
                  <p className="text-[11px] leading-snug text-muted-foreground/70">
                    Saldo × custo de compra. É o capital parado na prateleira.
                  </p>
                </div>
              </div>
              <p className="shrink-0 text-2xl font-bold text-blue-600">
                {allowed ? formatBRL(summary.investedValue) : 'R$ 7.310,00'}
              </p>
            </div>

            {/* Sem custo cadastrado o número sairia menor que a realidade: dizer
                quantos itens ficaram fora é obrigatório (no silent caps). */}
            {allowed && summary.missingCostCount > 0 && (
              <p className="flex items-start gap-1.5 pt-1 text-[11px] leading-snug text-amber-600">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                {summary.missingCostCount}{' '}
                {summary.missingCostCount === 1
                  ? 'item com saldo está sem custo informado e ficou fora desta conta'
                  : 'itens com saldo estão sem custo informado e ficaram fora desta conta'}{' '}
                — informe o custo no catálogo.
              </p>
            )}

            <div className="flex items-center justify-between gap-3 border-t pt-3">
              <div className="flex min-w-0 items-center gap-2">
                <Tag className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="text-sm text-muted-foreground">Potencial de venda</p>
                  <p className="text-[11px] leading-snug text-muted-foreground/70">
                    Saldo × preço de venda. É quanto pode entrar, não o que você investiu.
                  </p>
                </div>
              </div>
              <p className="shrink-0 text-2xl font-bold text-emerald-600">
                {allowed ? formatBRL(summary.salesValue) : 'R$ 12.480,00'}
              </p>
            </div>
          </Gate>
        </CardContent>
      </Card>

      {/* ===== 2. Movimentação no período, por motivo ===== */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-4 w-4" />
            Movimentação por motivo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            {allowed && !hasMovements ? (
              <AwaitingData
                hint={
                  loading
                    ? 'Carregando movimentações…'
                    : 'Nenhum movimento de estoque no período selecionado'
                }
              />
            ) : (
              <div className="space-y-4">
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={allowed ? byReason : SAMPLE_REASONS}
                      margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      {/* interval={0} força TODO motivo a ter rótulo: no padrão
                          o recharts esconde os que colidiriam, e uma barra sem
                          rótulo é indecifrável (medido em 360px: com os rótulos
                          curtos os 6 motivos cabem sem sobrepor). */}
                      <XAxis
                        dataKey="shortLabel"
                        interval={0}
                        tick={{ fontSize: 10 }}
                        className="text-muted-foreground"
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        tickFormatter={axisTick}
                        width={42}
                        className="text-muted-foreground"
                      />
                      <Tooltip
                        formatter={(value: number, name: string) => [formatQty(value), name]}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--popover))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={30}
                        formatter={(value) => <span className="text-xs">{value}</span>}
                      />
                      <Bar dataKey="inQty" name="Entradas" fill="#10b981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="outQty" name="Saídas" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="space-y-1">
                  {(allowed ? byReason : SAMPLE_REASONS).map((r) => (
                    <div
                      key={r.reason}
                      className="flex items-center justify-between gap-3 border-b py-2 last:border-0"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm">{r.label}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {r.count} {r.count === 1 ? 'movimento' : 'movimentos'}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-3 text-sm">
                        {r.inQty > 0 && (
                          <span className="flex items-center gap-1 text-emerald-600">
                            <ArrowUpCircle className="h-3.5 w-3.5" />
                            {formatQty(r.inQty)}
                          </span>
                        )}
                        {r.outQty > 0 && (
                          <span className="flex items-center gap-1 text-red-600">
                            <ArrowDownCircle className="h-3.5 w-3.5" />
                            {formatQty(r.outQty)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between gap-3 pt-2 text-sm font-semibold">
                    <span>Total no período</span>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="text-emerald-600">
                        +{formatQty(allowed ? totals.totalIn : 124)}
                      </span>
                      <span className="text-red-600">
                        −{formatQty(allowed ? totals.totalOut : 109)}
                      </span>
                    </div>
                  </div>
                  {/* Honestidade: un, kg e m² não se somam de verdade. */}
                  <p className="pt-1 text-[11px] leading-snug text-muted-foreground/70">
                    As quantidades somam produtos de unidades diferentes (un, kg, m²).
                  </p>
                </div>
              </div>
            )}
          </Gate>
        </CardContent>
      </Card>

      {/* ===== 3. Mais movimentados (giro) ===== */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ArrowDownCircle className="h-4 w-4" />
            Mais movimentados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            {allowed && moved.length === 0 ? (
              <AwaitingData hint="Nenhuma saída de estoque no período selecionado" />
            ) : (
              <div className="space-y-2">
                {(allowed ? moved : SAMPLE_MOVED).map((item, index) => (
                  <div
                    key={item.productId}
                    className="flex items-center justify-between gap-3 border-b py-2 last:border-0"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="w-4 shrink-0 text-xs text-muted-foreground">
                        {index + 1}.
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{item.name}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {item.count} {item.count === 1 ? 'movimento' : 'movimentos'}
                          {item.inQty > 0 ? ` · +${formatQty(item.inQty)} de entrada` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-semibold text-red-600">
                        −{formatQty(item.outQty)}
                      </p>
                      <p className="text-[11px] text-muted-foreground">saíram</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Gate>
        </CardContent>
      </Card>

      {/* ===== 4. Estoque parado ===== */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <PauseCircle className="h-4 w-4" />
            Estoque parado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            {allowed ? (
              idle.length === 0 ? (
                <div className="py-6 text-center">
                  <p className="text-sm font-medium text-emerald-600">Tudo girou no período</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Todo item rastreado teve ao menos um movimento.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    {idle.length} {idle.length === 1 ? 'item ficou' : 'itens ficaram'} sem nenhum
                    movimento no período.
                  </p>
                  {idle.slice(0, listLimit).map((unit) => (
                    <div
                      key={unit.id}
                      className="flex items-center justify-between gap-3 border-b py-2 last:border-0"
                    >
                      <p className="min-w-0 flex-1 truncate text-sm">{nameOf(unit)}</p>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold">
                          {formatQty(balanceOf(unit))}
                          {unit.unit ? (
                            <span className="ml-1 text-xs font-normal text-muted-foreground">
                              {unit.unit}
                            </span>
                          ) : null}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {formatBRLShort(Math.max(0, balanceOf(unit)) * Number(unit.price ?? 0))}{' '}
                          parados
                        </p>
                      </div>
                    </div>
                  ))}
                  <ListTail
                    total={idle.length}
                    shown={Math.min(idle.length, listLimit)}
                    noun="item"
                  />
                </div>
              )
            ) : (
              <div className="space-y-2">
                {['Broca 8mm', 'Fita isolante', 'Camiseta › GG'].map((name, i) => (
                  <div
                    key={name}
                    className="flex items-center justify-between border-b py-2 last:border-0"
                  >
                    <span className="text-sm">{name}</span>
                    <span className="text-sm">{12 - i * 3} un</span>
                  </div>
                ))}
              </div>
            )}
          </Gate>
        </CardContent>
      </Card>

      {/* ===== 5. Alertas ===== */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            Alertas de reposição
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            {allowed ? (
              alerts.all.length === 0 ? (
                <div className="py-6 text-center">
                  <p className="text-sm font-medium text-emerald-600">Nenhum alerta</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Nenhum item zerado, negativo ou abaixo do mínimo.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {alerts.all.slice(0, listLimit).map((row) => (
                    <div
                      key={row.unit.id}
                      className="flex items-center justify-between gap-3 border-b py-2 last:border-0"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{nameOf(row.unit)}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {row.min != null
                            ? `mínimo ${formatQty(row.min)}`
                            : 'sem mínimo definido'}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span
                          className={
                            row.balance < 0
                              ? 'text-sm font-semibold text-red-600'
                              : 'text-sm font-semibold'
                          }
                        >
                          {formatQty(row.balance)}
                          {row.unit.unit ? (
                            <span className="ml-1 text-xs font-normal text-muted-foreground">
                              {row.unit.unit}
                            </span>
                          ) : null}
                        </span>
                        <AlertBadge kind={row.kind} />
                      </div>
                    </div>
                  ))}
                  <ListTail
                    total={alerts.all.length}
                    shown={Math.min(alerts.all.length, listLimit)}
                    noun="alerta"
                  />
                </div>
              )
            ) : (
              <div className="space-y-2">
                {[
                  { n: 'Parafuso 3/8', q: '−4', k: 'Negativo' },
                  { n: 'Cabo HDMI', q: '0', k: 'Zerado' },
                  { n: 'Tinta branca 18L', q: '2', k: 'Abaixo do mínimo' },
                ].map((s) => (
                  <div
                    key={s.n}
                    className="flex items-center justify-between border-b py-2 last:border-0"
                  >
                    <span className="text-sm">{s.n}</span>
                    <span className="text-sm">
                      {s.q} · {s.k}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Gate>
        </CardContent>
      </Card>

      {!allowed && (
        <Card className="border-amber-300/60 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/10">
          <CardContent className="flex flex-col items-center py-6 text-center">
            <h3 className="text-sm font-semibold">Relatório de estoque</h3>
            <p className="mt-1 max-w-xs text-xs text-muted-foreground">
              Veja o que você tem, o que está acabando, o que mais girou no período e o que está
              parado na prateleira. Disponível no plano PRO.
            </p>
            <Button3D color="amber" size="sm" className="mt-4" onClick={onUnlock}>
              <Crown className="mr-2 h-4 w-4" />
              Desbloquear com PRO
            </Button3D>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===== peças =====

function AlertBadge({ kind }: { kind: StockAlertKind }) {
  const variant = kind === 'low' ? 'outline' : 'destructive';
  return (
    <Badge variant={variant} className="shrink-0 text-[10px]">
      {STOCK_ALERT_BADGE_LABELS[kind]}
    </Badge>
  );
}

interface KpiCardProps {
  icon: React.ReactNode;
  tint: string;
  label: string;
  value: string;
  valueClass: string;
  hint: string;
  allowed: boolean;
  onUnlock: () => void;
}

/**
 * Recebe `allowed`/`onUnlock` em vez de um componente-wrapper: um componente
 * definido no corpo do render tem identidade nova a cada render e faria o React
 * remontar a subárvore inteira toda vez.
 */
function KpiCard({ icon, tint, label, value, valueClass, hint, allowed, onUnlock }: KpiCardProps) {
  return (
    <Card>
      <CardContent className="p-3">
        <BlurOverlay allowed={allowed} onUnlock={onUnlock}>
          <div className="flex flex-col">
            <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full ${tint}`}>
              {icon}
            </div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className={`truncate text-base font-bold ${valueClass}`}>{value}</p>
            <p className="mt-0.5 truncate text-[11px] text-muted-foreground/70">{hint}</p>
          </div>
        </BlurOverlay>
      </CardContent>
    </Card>
  );
}
